import {
    DefaultProps,
    CompositeLayer,
    CompositeLayerProps,
    PickingInfo,
    LayerDataSource,
} from "@deck.gl/core"
import { get, set } from "nodash"
import { LayerId } from "../constants"
import { ShowLayerByIdMap } from "../types"
import { isMapToolActive } from "../pickingGuard"
import FlightPathsLayer from "./FlightPathsLayer.class"
import store from "integrations/redux/store"
import {
    GenericImage,
    ID,
    ImagePreviewType,
    ImageProcessingColors,
    ImageProcessingState,
} from "@nova/constants"
import {
    selectIsOrthoFlightUploading,
    selectOrthoImagesAndUploadsWithContextByFlightId,
    selectOrthoImagesByFlightId,
} from "features/entity-caching/images/orthos/selectors"
import FlightImagePointsLayer from "./FlightImagePointsLayer"
import { setActiveImageId, setHoveredImageId } from "../slice"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import {
    selectInfraredImagesAndUploadsWithContextByFlightId,
    selectInfraredImagesByFlightId,
    selectIsInfraredFlightUploading,
} from "features/entity-caching/images/hotspots/selectors"
import {
    selecteColorfinderImagesAndUploadsWithContextByFlightId,
    selectIsImageColorfinderFlightUploading,
} from "features/entity-caching/images/colorfinder/selectors"
import { getGlobalMapRef, getViewport } from "features/map/GlobalViewState"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"

export type FlightPath = { path: [number, number, number][]; id: string }
export type FlightPoint = {
    point: [number, number, number]
    id: string
    imageId?: string
    takenAt: number
    state: ImageProcessingState
    isErrored?: boolean
    warningColor?: string
    fileName?: string
    imagePreviewType?: ImagePreviewType
}

type _FlightsLayerProps = {
    filterMaps?: {
        byFlight: ShowLayerByIdMap
        byPreview: ShowLayerByIdMap
    }
    pdfSnapshotOpen?: boolean
    imageColorfinderOpen?: boolean
    activeImageIds: Record<string, boolean>
    hoveredImageId?: ID
}

const MIN_VALID_ALT = 10
const MAX_VALID_ALT = 20000
const DEFAULT_ALT_AGL = 100

export type FlightsLayerProps = CompositeLayerProps & _FlightsLayerProps

const defaultProps: DefaultProps<FlightsLayerProps> = {
    filterMaps: {
        byFlight: {},
        byPreview: {},
    },
}

const getFilterValueFromFlightFilterMap =
    (key: string, props: FlightsLayerProps) => (d: FlightPoint[]) => {
        const flightId = get(d, key) as string
        const result =
            get(props, ["filterMaps", "byFlight", flightId]) ||
            get(props, ["filterMaps", "byPreview", flightId])
        const numResult = result ? 1 : 0

        return numResult
    }
/**
 * a composite class to render the points and labels for a set of hotspots
 */
export default class FlightsLayer<ExtraPropsType = FlightsLayerProps> extends CompositeLayer<
    ExtraPropsType & Required<_FlightsLayerProps>
> {
    state = {} as {
        subLayerData: LayerDataSource<FlightPath>
        flightImagePoints: FlightPoint[]
        isUploadingMap: Record<ID, boolean>
    }
    static defaultProps = defaultProps
    static layerName = "FlightsLayer" as string

    updateState({ changeFlags }: { changeFlags: Record<string, unknown> }) {
        if (changeFlags.dataChanged) {
            const data = get(this.props, "data", []) as string[]
            const state = store.getState()

            const subLayerData = [] as FlightPath[]
            const flightImagePoints = [] as Omit<FlightPoint, "takenAt">[]
            const isUploadingMap = {} as Record<ID, boolean>

            data.forEach((id) => {
                let images
                if (this.props.pdfSnapshotOpen || this.props.imageColorfinderOpen) {
                    images =
                        selectInfraredImagesByFlightId(id)(state).length > 0
                            ? selectInfraredImagesByFlightId(id)(state)
                            : selectOrthoImagesByFlightId(id)(state)
                } else {
                    const infrared = selectInfraredImagesAndUploadsWithContextByFlightId(id)(state)
                    const ortho = selectOrthoImagesAndUploadsWithContextByFlightId(id)(state)
                    const colorfinder =
                        selecteColorfinderImagesAndUploadsWithContextByFlightId(id)(state)
                    images = infrared.length ? infrared : ortho.length ? ortho : colorfinder
                }
                const isUploading =
                    selectIsOrthoFlightUploading(id)(state) ||
                    selectIsInfraredFlightUploading(id)(state) ||
                    selectIsImageColorfinderFlightUploading(id)(state)
                isUploadingMap[id] = isUploading
                if (!images) {
                    return
                }

                const path = [] as Omit<FlightPoint, "id">[]

                const is3dTerrainEnabled = getGlobalTerrainData()?.terrainState
                const mapRef = getGlobalMapRef()?.current

                images.forEach((image) => {
                    if (image) {
                        const { longitude, latitude, takenAt, altitude } = image as GenericImage

                        // for 2d map: alt is always default
                        // for 3d map: alt agl is calculated from altitude - terrain
                        let altAgl = DEFAULT_ALT_AGL
                        if (
                            is3dTerrainEnabled &&
                            altitude &&
                            altitude >= MIN_VALID_ALT &&
                            altitude <= MAX_VALID_ALT
                        ) {
                            const elev = mapRef?.queryTerrainElevation([longitude, latitude])
                            if (elev) {
                                altAgl = Math.max(altitude - elev, MIN_VALID_ALT)
                            }
                        }

                        const state = get(image, "state", ImageProcessingState.IngestDone)
                        const fileName = get(image, "fileName", "")
                        flightImagePoints.push({
                            point: [longitude, latitude, altAgl],
                            id,
                            imageId: image.id,
                            state: state,
                            fileName: fileName,
                            isErrored: !!get(image, "error"),
                            warningColor: get(image, "warningColor", ""),
                            imagePreviewType: get(
                                image,
                                "imagePreviewType",
                                ""
                            ) as ImagePreviewType,
                        })
                        path.push({
                            point: [longitude, latitude, altAgl],
                            takenAt: takenAt || 0,
                            state: state,
                        })
                    }
                })
                subLayerData.push({
                    path: path.sort((a, b) => a.takenAt - b.takenAt).map((e) => e.point),
                    id,
                })
            })

            this.setState({ subLayerData, flightImagePoints, isUploadingMap })
        }
    }

    onClick(info: PickingInfo, event: MouseEvent) {
        if (isMapToolActive()) return false

        set(event, "clickedFlightpathImage", true)
        const imageId = get(info, "object.imageId") as string
        const imagePreviewType = get(info, "object.imagePreviewType") as ImagePreviewType
        store.dispatch(setActiveImageId({ ids: { [imageId]: true }, type: imagePreviewType }))
        return true
    }

    onHover(info: PickingInfo): boolean {
        const imageId = get(info, "object.imageId") as string
        if (imageId) store.dispatch(setHoveredImageId(imageId))
        else store.dispatch(setHoveredImageId(undefined))
        return true
    }

    renderLayers() {
        return [
            new FlightPathsLayer({
                id: "flight-paths",
                data: this.state.subLayerData,
                visible: true,
                terrainDrawMode: "offset",
                extensions: [...getGlobalTerrainData().extensions],
                updateTriggers: {
                    getFilterValue: this.props.filterMaps,
                },
                getFilterValue: getFilterValueFromFlightFilterMap("id", this.props),
                getColor: () => [30, 109, 169],
            }),
            new FlightImagePointsLayer({
                id: LayerId.FlightPathImagePoints,
                data: this.state.flightImagePoints,
                visible: true,
                extensions: [...getGlobalTerrainData().extensions],
                pickable: !this.props.pdfSnapshotOpen && this.props.pickable !== false,
                updateTriggers: {
                    getFilterValue: this.props.filterMaps,
                },
                radiusMinPixels: 1,
                radiusMaxPixels: 12,
                getPosition: (d) => {
                    return d.point
                },
                getFilterValue: getFilterValueFromFlightFilterMap("id", this.props),
                getFillColor: (d: FlightPoint) => {
                    const imageId = d.imageId
                    const state = d.state
                    if (!imageId) return [255, 255, 255, 255]
                    if (
                        (!!imageId && this.props.activeImageIds[imageId]) ||
                        this.props.hoveredImageId === imageId
                    ) {
                        return [39, 187, 255, 255]
                    }
                    if (d.isErrored) return convertHexStringToRGBArray(ImageProcessingColors.error)
                    if (d.warningColor) return convertHexStringToRGBArray(d.warningColor)
                    if (!this.state.isUploadingMap[d.id]) return [255, 255, 255, 255]
                    if (!state) return [255, 255, 255, 255]
                    if (state === ImageProcessingState.IngestDone) {
                        return convertHexStringToRGBArray(ImageProcessingColors.Complete)
                    }
                    if (state >= ImageProcessingState.Uploading) {
                        return convertHexStringToRGBArray(ImageProcessingColors.Uploading)
                    }
                    return [255, 255, 255, 255]
                },
                getLineColor: (d: FlightPoint) => {
                    if (getViewport().zoom < 15) {
                        if (d.isErrored)
                            return convertHexStringToRGBArray(ImageProcessingColors.error)
                        if (d.warningColor) return convertHexStringToRGBArray(d.warningColor)
                        if (!this.state.isUploadingMap[d.id]) return [30, 109, 169, 255]
                        if (d.state === ImageProcessingState.IngestDone) {
                            return convertHexStringToRGBArray(ImageProcessingColors.Complete)
                        }
                        if (d.state >= ImageProcessingState.Uploading) {
                            return convertHexStringToRGBArray(ImageProcessingColors.Uploading)
                        }
                    }
                    if (!!d.imageId && this.props.activeImageIds[d.imageId]) {
                        return [255, 255, 255, 255]
                    }
                    if (this.props.hoveredImageId === d.imageId) {
                        return [8, 37, 51, 255]
                    }
                    return [30, 109, 169, 255]
                },
                parameters: {
                    depthTest: false,
                },
            }),
        ]
    }
}
